import {
  DockWeatherMeasurement,
  FlightWeatherData,
  FlightWeatherEvaluation,
  FlightWeatherHourly,
} from '../types/flight-weather'

export const FLIGHT_WEATHER_WIND_LIMIT_MPS = 12

function finiteNumber (value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

function hasCompleteDockMeasurement (dock: DockWeatherMeasurement) {
  const rainfall = finiteNumber(dock.rainfall)
  const humidity = finiteNumber(dock.humidityPercent)
  return finiteNumber(dock.windSpeedMps) !== null &&
    rainfall !== null && rainfall >= 0 && rainfall <= 3 &&
    finiteNumber(dock.temperatureC) !== null &&
    humidity !== null && humidity >= 0 && humidity <= 100
}

function hasCompleteNowWeather (weather: FlightWeatherData) {
  const now = weather.now
  const humidity = finiteNumber(now?.humidity_percent)
  return !!now && !!now.observed_at && !!now.text &&
    finiteNumber(now.wind_speed_mps) !== null &&
    finiteNumber(now.precipitation_mm) !== null &&
    finiteNumber(now.temperature_c) !== null &&
    humidity !== null && humidity >= 0 && humidity <= 100 &&
    finiteNumber(now.pressure_hpa) !== null
}

function hasCompleteHourlyWeather (hour: FlightWeatherHourly) {
  const probability = finiteNumber(hour?.precipitation_probability)
  const humidity = finiteNumber(hour?.humidity_percent)
  return !!hour && !!hour.forecast_at && !!hour.text &&
    finiteNumber(hour.wind_speed_mps) !== null &&
    finiteNumber(hour.precipitation_mm) !== null &&
    finiteNumber(hour.temperature_c) !== null &&
    probability !== null && probability >= 0 && probability <= 100 &&
    humidity !== null && humidity >= 0 && humidity <= 100
}

function addReason (reasons: string[], reason: string) {
  if (!reasons.includes(reason)) {
    reasons.push(reason)
  }
}

export function evaluateFlightWeather (
  dock: DockWeatherMeasurement,
  weather: FlightWeatherData | null,
  weatherError = ''
): FlightWeatherEvaluation {
  const reasons: string[] = []

  if (dock.offline) {
    addReason(reasons, '机场离线，无法确认实时飞行环境')
  }
  if (!hasCompleteDockMeasurement(dock)) {
    addReason(reasons, '机场实时天气数据不完整')
  } else {
    const windSpeed = finiteNumber(dock.windSpeedMps)!
    const rainfall = finiteNumber(dock.rainfall)!
    if (windSpeed >= FLIGHT_WEATHER_WIND_LIMIT_MPS) {
      addReason(reasons, `机场实测风速 ${windSpeed.toFixed(1)} m/s，达到限制值`)
    }
    if (rainfall > 0) {
      addReason(reasons, '机场实时检测到降雨')
    }
  }

  if (!weather) {
    addReason(reasons, weatherError || '和风天气数据尚未就绪')
    return { recommended: false, reasons }
  }
  if (weather.stale) {
    addReason(reasons, '和风天气数据已过期')
  }
  if (!hasCompleteNowWeather(weather) || !Array.isArray(weather.hourly) || weather.hourly.length < 2 ||
    weather.hourly.slice(0, 2).some(hour => !hasCompleteHourlyWeather(hour))) {
    addReason(reasons, '和风天气关键数据不完整')
    return { recommended: false, reasons }
  }

  if (weather.now.wind_speed_mps >= FLIGHT_WEATHER_WIND_LIMIT_MPS) {
    addReason(reasons, `和风实况风速 ${weather.now.wind_speed_mps.toFixed(1)} m/s，达到限制值`)
  }
  if (weather.now.precipitation_mm > 0) {
    addReason(reasons, '和风实况显示当前有降水')
  }
  weather.hourly.slice(0, 2).forEach((hour, index) => {
    if (hour.wind_speed_mps >= FLIGHT_WEATHER_WIND_LIMIT_MPS) {
      addReason(reasons, `未来 ${index + 1} 小时风速 ${hour.wind_speed_mps.toFixed(1)} m/s，达到限制值`)
    }
    if (hour.precipitation_mm > 0) {
      addReason(reasons, `未来 ${index + 1} 小时预计有降水`)
    }
  })

  return { recommended: reasons.length === 0, reasons }
}
